/**
 * Payments and invoices, plus the MRR chart from the comp.
 *
 * Preview only — Estate Hub has no billing domain. See `mock/revenue.ts`.
 */
import { useMemo, useState } from 'react';
import { CurrencyInr, TrendUp, WarningCircle } from '@phosphor-icons/react';
import { INVOICES, MRR_SERIES, type InvoiceStatus } from '../mock/revenue';
import { formatDate, inr, inrCompact } from '../lib/format';
import { Badge, PreviewNote, Segmented, type Tone } from '../components/ui';

const STATUS_TONE: Record<InvoiceStatus, Tone> = { paid: 'ok', due: 'info', overdue: 'bad' };
const COLUMNS = '1.1fr 1.6fr 1fr 1fr 1fr .9fr';

export default function Billing() {
  const [range, setRange] = useState<'6m' | '12m'>('12m');
  const [filter, setFilter] = useState<InvoiceStatus | 'all'>('all');

  const series = range === '6m' ? MRR_SERIES.slice(6) : MRR_SERIES;
  const peak = Math.max(...series.map((point) => point.value));
  const current = MRR_SERIES[MRR_SERIES.length - 1].value;
  const previous = MRR_SERIES[MRR_SERIES.length - 2].value;
  const growth = (((current - previous) / previous) * 100).toFixed(1);

  const rows = useMemo(
    () => (filter === 'all' ? INVOICES : INVOICES.filter((i) => i.status === filter)),
    [filter],
  );

  const totals = useMemo(() => {
    const overdue = INVOICES.filter((i) => i.status === 'overdue');
    const due = INVOICES.filter((i) => i.status === 'due');
    return {
      overdueCount: overdue.length,
      overdueAmount: overdue.reduce((sum, i) => sum + i.amount, 0),
      dueCount: due.length,
      dueAmount: due.reduce((sum, i) => sum + i.amount, 0),
    };
  }, []);

  return (
    <div className="stack animate-up" style={{ gap: 18 }}>
      <PreviewNote>
        <strong>Preview, not connected.</strong> There is no invoice, payment or MRR data in the
        Estate Hub API; every figure below comes from <code>src/mock/revenue.ts</code>. Treat this
        as the layout these screens will use once billing exists.
      </PreviewNote>

      <div className="grid grid-3">
        <div className="card card-pad">
          <div className="icon-tile" style={{ marginBottom: 14 }}>
            <CurrencyInr size={19} />
          </div>
          <p className="num" style={{ margin: 0, fontSize: 28, lineHeight: 1 }}>
            {inrCompact(current)}
          </p>
          <p className="t-md strong" style={{ margin: '9px 0 0' }}>
            Monthly recurring revenue
          </p>
          <div className="row row-tight" style={{ marginTop: 5 }}>
            <TrendUp size={13} color="var(--ok)" />
            <span className="t-xs" style={{ color: 'var(--ok)' }}>
              +{growth}% on last month
            </span>
          </div>
        </div>

        <div className="card card-pad">
          <div
            className="icon-tile"
            style={{ marginBottom: 14, background: 'var(--bad-bg)', color: 'var(--bad)' }}
          >
            <WarningCircle size={19} />
          </div>
          <p className="num" style={{ margin: 0, fontSize: 28, lineHeight: 1 }}>
            {totals.overdueCount}
          </p>
          <p className="t-md strong" style={{ margin: '9px 0 0' }}>
            Overdue invoices
          </p>
          <p className="t-xs faint" style={{ margin: '3px 0 0' }}>
            {inr(totals.overdueAmount)} outstanding
          </p>
        </div>

        <div className="card card-pad">
          <div
            className="icon-tile"
            style={{ marginBottom: 14, background: 'var(--info-bg)', color: 'var(--info)' }}
          >
            <CurrencyInr size={19} />
          </div>
          <p className="num" style={{ margin: 0, fontSize: 28, lineHeight: 1 }}>
            {totals.dueCount}
          </p>
          <p className="t-md strong" style={{ margin: '9px 0 0' }}>
            Due this cycle
          </p>
          <p className="t-xs faint" style={{ margin: '3px 0 0' }}>
            {inr(totals.dueAmount)} expected
          </p>
        </div>
      </div>

      {/* ---- MRR chart ---- */}
      <div className="card">
        <div className="card-head">
          <div>
            <h2 className="card-title">Recurring revenue</h2>
            <p className="card-sub">Billed monthly, across every tenant</p>
          </div>
          <div className="spacer" />
          <Segmented
            value={range}
            onChange={setRange}
            options={[
              { id: '6m', label: '6 months' },
              { id: '12m', label: '12 months' },
            ]}
          />
        </div>
        <div className="card-pad">
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 10,
              height: 190,
              paddingTop: 8,
            }}
            role="img"
            aria-label={`Monthly recurring revenue over the last ${series.length} months, ending at ${inrCompact(current)}`}
          >
            {series.map((point, index) => {
              const isLast = index === series.length - 1;
              return (
                <div
                  key={point.month}
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 8,
                    height: '100%',
                    justifyContent: 'flex-end',
                  }}
                >
                  <span className="t-xs" style={{ color: isLast ? 'var(--ink)' : 'var(--faint)' }}>
                    {inrCompact(point.value)}
                  </span>
                  <div
                    style={{
                      width: '100%',
                      height: `${(point.value / peak) * 100}%`,
                      background: isLast ? 'var(--ac)' : 'var(--ac-tint-2)',
                      borderRadius: '8px 8px 4px 4px',
                      minHeight: 4,
                    }}
                  />
                  <span
                    className="t-xs"
                    style={{ color: isLast ? 'var(--ink)' : 'var(--faint)', fontWeight: isLast ? 600 : 400 }}
                  >
                    {point.month}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ---- Invoices ---- */}
      <div className="card">
        <div className="card-head">
          <Segmented
            value={filter}
            onChange={setFilter}
            options={[
              { id: 'all', label: 'All', count: INVOICES.length },
              { id: 'overdue', label: 'Overdue', count: totals.overdueCount },
              { id: 'due', label: 'Due', count: totals.dueCount },
              { id: 'paid', label: 'Paid', count: INVOICES.filter((i) => i.status === 'paid').length },
            ]}
          />
        </div>

        <div className="table-scroll">
          <div>
            <div className="table-head" style={{ gridTemplateColumns: COLUMNS }}>
              <span>Invoice</span>
              <span>Agency</span>
              <span>Plan</span>
              <span>Amount</span>
              <span>Due</span>
              <span style={{ textAlign: 'right' }}>Status</span>
            </div>

            {rows.map((invoice) => (
              <div key={invoice.id} className="table-row" style={{ gridTemplateColumns: COLUMNS }}>
                <span className="t-sm strong">{invoice.number}</span>
                <span className="truncate t-md">{invoice.agency}</span>
                <span className="t-sm muted">{invoice.plan}</span>
                <span className="num" style={{ fontSize: 13.5 }}>
                  {inr(invoice.amount)}
                </span>
                <span className="t-sm muted">{formatDate(invoice.due)}</span>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Badge tone={STATUS_TONE[invoice.status]}>{invoice.status}</Badge>
                </div>
              </div>
            ))}

            <div className="table-foot">
              <p className="t-sm muted" style={{ margin: 0 }}>
                Showing {rows.length} of {INVOICES.length} invoices
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
